import React, { useState, useEffect } from 'react';
import { 
  User, 
  Bell, 
  Shield, 
  Palette, 
  Database, 
  Download, 
  Upload, 
  Save, 
  Eye, 
  EyeOff,
  Mail,
  Phone,
  Building,
  MapPin,
  Globe,
  CreditCard,
  FileText,
  Settings as SettingsIcon,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../lib/database/auth';

interface SettingsTab {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  adminOnly?: boolean;
}

const settingsTabs: SettingsTab[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'business', label: 'Business Info', icon: Building },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'data', label: 'Data & Export', icon: Database, adminOnly: true },
];

export const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Profile settings
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    address: '',
  });

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    invoiceReminders: true,
    paymentAlerts: true,
    overdueNotifications: true,
    weeklyReports: false,
    marketingEmails: false,
  });

  // Security settings
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
  });

  // Appearance settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: 'light',
    colorScheme: 'blue',
    compactMode: false,
    sidebarCollapsed: false,
  });

  // Business settings
  const [businessData, setBusinessData] = useState({
    businessName: '',
    businessType: '',
    industry: '',
    website: '',
    taxId: '',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    fiscalYearStart: 'April',
  });

  // Filter tabs based on user role
  const availableTabs = settingsTabs.filter(tab => 
    !tab.adminOnly || user?.role === 'admin'
  );

  // Initialize form data
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        company_name: user.company_name || '',
        address: user.address || '',
      });
    }
  }, [user]);

  // Redirect non-admin users away from data tab
  useEffect(() => {
    if (activeTab === 'data' && user?.role !== 'admin') {
      setActiveTab('profile');
    }
  }, [activeTab, user?.role]);

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!profileData.full_name.trim()) {
        setError('Full name is required');
        setLoading(false);
        return;
      }

      if (!profileData.email.trim()) {
        setError('Email is required');
        setLoading(false);
        return;
      }

      const { data, error: updateError } = await authService.updateProfile(user?.id || '', {
        full_name: profileData.full_name.trim(),
        email: profileData.email.trim(),
        phone: profileData.phone.trim() || null,
        company_name: profileData.company_name.trim() || null,
        address: profileData.address.trim() || null,
      });

      if (updateError) {
        setError('Failed to update profile: ' + updateError.message);
        setLoading(false);
        return;
      }

      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!securityData.currentPassword) {
        setError('Current password is required');
        setLoading(false);
        return;
      }

      if (!securityData.newPassword) {
        setError('New password is required');
        setLoading(false);
        return;
      }

      if (securityData.newPassword.length < 6) {
        setError('New password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      if (securityData.newPassword !== securityData.confirmPassword) {
        setError('New passwords do not match');
        setLoading(false);
        return;
      }

      // Note: Password change would require additional Supabase auth methods
      setSuccess('Password change functionality will be implemented with proper authentication flow');
      
      // Reset form
      setSecurityData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorEnabled: securityData.twoFactorEnabled,
      });

    } catch (error: any) {
      setError('An unexpected error occurred: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    // Mock export functionality
    setSuccess('Data export will be available soon. You will receive an email with your data.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
          <User className="w-10 h-10 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">{user?.full_name || 'User'}</h3>
          <p className="text-gray-600 capitalize">{user?.role} Account</p>
          <p className="text-sm text-gray-500">{user?.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label="Full Name" icon={User} required>
          <input
            type="text"
            value={profileData.full_name}
            onChange={(e) => handleInputChange('full_name', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter your full name"
          />
        </FormField>

        <FormField label="Email Address" icon={Mail} required>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter your email"
          />
        </FormField>

        <FormField label="Phone Number" icon={Phone}>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter your phone number"
          />
        </FormField>

        <FormField label="Company Name" icon={Building}>
          <input
            type="text"
            value={profileData.company_name}
            onChange={(e) => handleInputChange('company_name', e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter your company name"
          />
        </FormField>
      </div>

      <FormField label="Address" icon={MapPin}>
        <textarea
          value={profileData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          rows={3}
          placeholder="Enter your address"
        />
      </FormField>

      <div className="flex justify-end">
        <Button
          onClick={handleProfileUpdate}
          loading={loading}
          icon={Save}
          className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
        >
          Update Profile
        </Button>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive general email notifications' },
            { key: 'invoiceReminders', label: 'Invoice Reminders', description: 'Get notified about invoice due dates' },
            { key: 'paymentAlerts', label: 'Payment Alerts', description: 'Receive alerts when payments are received' },
            { key: 'overdueNotifications', label: 'Overdue Notifications', description: 'Get notified about overdue invoices' },
            { key: 'weeklyReports', label: 'Weekly Reports', description: 'Receive weekly business summary reports' },
            { key: 'marketingEmails', label: 'Marketing Emails', description: 'Receive product updates and tips' },
          ].map((setting) => (
            <div key={setting.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">{setting.label}</p>
                <p className="text-sm text-gray-600">{setting.description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationSettings[setting.key as keyof typeof notificationSettings]}
                  onChange={(e) => setNotificationSettings(prev => ({ 
                    ...prev, 
                    [setting.key]: e.target.checked 
                  }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => {
            setSuccess('Notification preferences saved!');
            setTimeout(() => setSuccess(''), 3000);
          }}
          icon={Save}
          className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
        >
          Save Preferences
        </Button>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
        <div className="space-y-4">
          <FormField label="Current Password" icon={Shield} required>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={securityData.currentPassword}
                onChange={(e) => setSecurityData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </FormField>

          <FormField label="New Password" icon={Shield} required>
            <input
              type={showPassword ? 'text' : 'password'}
              value={securityData.newPassword}
              onChange={(e) => setSecurityData(prev => ({ ...prev, newPassword: e.target.value }))}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter new password"
            />
          </FormField>

          <FormField label="Confirm New Password" icon={Shield} required>
            <input
              type={showPassword ? 'text' : 'password'}
              value={securityData.confirmPassword}
              onChange={(e) => setSecurityData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Confirm new password"
            />
          </FormField>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handlePasswordChange}
            loading={loading}
            icon={Save}
            className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
          >
            Change Password
          </Button>
        </div>
      </div>

      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Two-Factor Authentication</h3>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">Two-Factor Authentication</p>
            <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={securityData.twoFactorEnabled}
              onChange={(e) => setSecurityData(prev => ({ 
                ...prev, 
                twoFactorEnabled: e.target.checked 
              }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>
    </div>
  );

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Theme Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField label="Theme">
            <select
              value={appearanceSettings.theme}
              onChange={(e) => setAppearanceSettings(prev => ({ ...prev, theme: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto (System)</option>
            </select>
          </FormField>

          <FormField label="Color Scheme">
            <select
              value={appearanceSettings.colorScheme}
              onChange={(e) => setAppearanceSettings(prev => ({ ...prev, colorScheme: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="blue">Blue</option>
              <option value="green">Green</option>
              <option value="purple">Purple</option>
              <option value="orange">Orange</option>
            </select>
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Layout Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Compact Mode</p>
              <p className="text-sm text-gray-600">Use smaller spacing and elements</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={appearanceSettings.compactMode}
                onChange={(e) => setAppearanceSettings(prev => ({ 
                  ...prev, 
                  compactMode: e.target.checked 
                }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Collapsed Sidebar</p>
              <p className="text-sm text-gray-600">Start with sidebar collapsed</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={appearanceSettings.sidebarCollapsed}
                onChange={(e) => setAppearanceSettings(prev => ({ 
                  ...prev, 
                  sidebarCollapsed: e.target.checked 
                }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => {
            setSuccess('Appearance settings saved!');
            setTimeout(() => setSuccess(''), 3000);
          }}
          icon={Save}
          className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
        >
          Save Settings
        </Button>
      </div>
    </div>
  );

  const renderBusinessTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField label="Business Name" icon={Building} required>
          <input
            type="text"
            value={businessData.businessName}
            onChange={(e) => setBusinessData(prev => ({ ...prev, businessName: e.target.value }))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Enter business name"
          />
        </FormField>

        <FormField label="Business Type" icon={Building}>
          <select
            value={businessData.businessType}
            onChange={(e) => setBusinessData(prev => ({ ...prev, businessType: e.target.value }))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select business type</option>
            <option value="sole_proprietorship">Sole Proprietorship</option>
            <option value="partnership">Partnership</option>
            <option value="llp">Limited Liability Partnership</option>
            <option value="private_limited">Private Limited Company</option>
            <option value="public_limited">Public Limited Company</option>
          </select>
        </FormField>

        <FormField label="Industry" icon={Building}>
          <select
            value={businessData.industry}
            onChange={(e) => setBusinessData(prev => ({ ...prev, industry: e.target.value }))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Select industry</option>
            <option value="technology">Technology</option>
            <option value="consulting">Consulting</option>
            <option value="retail">Retail</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="healthcare">Healthcare</option>
            <option value="education">Education</option>
            <option value="finance">Finance</option>
            <option value="other">Other</option>
          </select>
        </FormField>

        <FormField label="Website" icon={Globe}>
          <input
            type="url"
            value={businessData.website}
            onChange={(e) => setBusinessData(prev => ({ ...prev, website: e.target.value }))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://example.com"
          />
        </FormField>

        <FormField label="Tax ID / GSTIN" icon={FileText}>
          <input
            type="text"
            value={businessData.taxId}
            onChange={(e) => setBusinessData(prev => ({ ...prev, taxId: e.target.value.toUpperCase() }))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="22AAAAA0000A1Z5"
          />
        </FormField>

        <FormField label="Currency" icon={CreditCard}>
          <select
            value={businessData.currency}
            onChange={(e) => setBusinessData(prev => ({ ...prev, currency: e.target.value }))}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="INR">Indian Rupee (₹)</option>
            <option value="USD">US Dollar ($)</option>
            <option value="EUR">Euro (€)</option>
            <option value="GBP">British Pound (£)</option>
          </select>
        </FormField>

        <FormField label="Timezone">
          <select
            value={businessData.timezone}
            onChange={(e) => setBusinessData(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
            <option value="America/New_York">America/New_York (EST)</option>
            <option value="Europe/London">Europe/London (GMT)</option>
            <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
          </select>
        </FormField>

        <FormField label="Fiscal Year Start">
          <select
            value={businessData.fiscalYearStart}
            onChange={(e) => setBusinessData(prev => ({ ...prev, fiscalYearStart: e.target.value }))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="January">January</option>
            <option value="April">April</option>
            <option value="July">July</option>
            <option value="October">October</option>
          </select>
        </FormField>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => {
            setSuccess('Business information saved!');
            setTimeout(() => setSuccess(''), 3000);
          }}
          icon={Save}
          className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800"
        >
          Save Business Info
        </Button>
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-success-50 to-success-100 border border-success-200 p-6 rounded-lg">
        <h3 className="text-success-800 font-semibold mb-2">Free Plan</h3>
        <p className="text-success-700 mb-4">You're currently on the free plan with full access to all features.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-medium text-success-800">Invoices</p>
            <p className="text-success-600">Unlimited</p>
          </div>
          <div>
            <p className="font-medium text-success-800">Clients</p>
            <p className="text-success-600">Unlimited</p>
          </div>
          <div>
            <p className="font-medium text-success-800">Storage</p>
            <p className="text-success-600">5GB</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing History</h3>
        <div className="bg-gray-50 p-6 rounded-lg text-center">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No billing history available</p>
          <p className="text-sm text-gray-500 mt-2">You're on the free plan</p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
        <div className="bg-gray-50 p-6 rounded-lg text-center">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No payment methods added</p>
          <Button variant="secondary" size="sm" className="mt-4">
            Add Payment Method
          </Button>
        </div>
      </div>
    </div>
  );

  const renderDataTab = () => {
    // Only show for admin users
    if (user?.role !== 'admin') {
      return (
        <div className="text-center py-12">
          <Lock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Access Restricted</h3>
          <p className="text-gray-600">This section is only available to administrators.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center space-x-3 mb-3">
                <Download className="w-6 h-6 text-primary-600" />
                <h4 className="font-medium text-gray-900">Export All Data</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">Download all your invoices, quotes, clients, and payments in CSV format.</p>
              <Button size="sm" onClick={handleExportData} className="w-full">
                Export All Data
              </Button>
            </Card>

            <Card className="p-4 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center space-x-3 mb-3">
                <FileText className="w-6 h-6 text-secondary-600" />
                <h4 className="font-medium text-gray-900">Export Invoices</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">Download only your invoice data with detailed line items.</p>
              <Button size="sm" variant="secondary" className="w-full">
                Export Invoices
              </Button>
            </Card>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Data</h3>
          <Card className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Upload className="w-6 h-6 text-warning-600" />
              <h4 className="font-medium text-gray-900">Import from CSV</h4>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Import clients, products, or other data from CSV files. Make sure your CSV follows our template format.
            </p>
            <div className="flex space-x-3">
              <Button variant="secondary" size="sm">
                Download Template
              </Button>
              <Button size="sm" className="bg-warning-600 hover:bg-warning-700">
                Import Data
              </Button>
            </div>
          </Card>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Management</h3>
          <div className="bg-error-50 border border-error-200 p-6 rounded-lg">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-error-600" />
              <h4 className="text-error-800 font-semibold">Danger Zone</h4>
            </div>
            <p className="text-error-700 text-sm mb-4">
              These actions are irreversible and will permanently delete data. Please be certain before proceeding.
            </p>
            <div className="space-y-3">
              <Button 
                variant="error" 
                size="sm"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete all business data? This action cannot be undone.')) {
                    setSuccess('Data deletion would be implemented with proper confirmation flow');
                    setTimeout(() => setSuccess(''), 3000);
                  }
                }}
              >
                Delete All Business Data
              </Button>
              <Button 
                variant="error" 
                size="sm"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
                    setSuccess('Account deletion would be implemented with proper confirmation flow');
                    setTimeout(() => setSuccess(''), 3000);
                  }
                }}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRestrictedDataTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
        <div className="flex items-center space-x-3 mb-4">
          <Download className="w-6 h-6 text-blue-600" />
          <h3 className="text-blue-800 font-semibold">Data Export</h3>
        </div>
        <p className="text-blue-700 mb-4">
          You can export your personal data and account information.
        </p>
        <Button 
          size="sm" 
          className="bg-blue-600 hover:bg-blue-700"
          onClick={() => {
            setSuccess('Your personal data export will be sent to your email address.');
            setTimeout(() => setSuccess(''), 3000);
          }}
        >
          Export My Data
        </Button>
      </div>

      <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg">
        <div className="flex items-center space-x-3 mb-4">
          <Lock className="w-6 h-6 text-gray-500" />
          <h3 className="text-gray-700 font-semibold">Administrative Functions</h3>
        </div>
        <p className="text-gray-600 mb-4">
          Advanced data management features are only available to administrators. 
          Contact your system administrator if you need access to bulk data operations.
        </p>
        <div className="text-sm text-gray-500">
          <p>• Bulk data export/import</p>
          <p>• System-wide data management</p>
          <p>• Account deletion privileges</p>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'security':
        return renderSecurityTab();
      case 'appearance':
        return renderAppearanceTab();
      case 'business':
        return renderBusinessTab();
      case 'billing':
        return renderBillingTab();
      case 'data':
        return user?.role === 'admin' ? renderDataTab() : renderRestrictedDataTab();
      default:
        return renderProfileTab();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
          <SettingsIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account preferences and configurations</p>
        </div>
      </div>

      {/* Alerts */}
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <Card padding="sm">
            <nav className="space-y-1">
              {availableTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 text-left rounded-lg transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'bg-primary-100 text-primary-700 border-r-2 border-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                    {tab.adminOnly && user?.role !== 'admin' && (
                      <Lock className="w-4 h-4 text-gray-400 ml-auto" />
                    )}
                  </button>
                );
              })}
            </nav>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <Card>
            <div className="border-b border-gray-200 pb-4 mb-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {availableTabs.find(tab => tab.id === activeTab)?.label}
                </h2>
                {activeTab === 'data' && user?.role !== 'admin' && (
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Lock className="w-4 h-4" />
                    <span>Limited Access</span>
                  </div>
                )}
              </div>
            </div>
            {renderTabContent()}
          </Card>
        </div>
      </div>
    </div>
  );
};